import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as _ from "lodash-es";
import IOrder_Progress_State from "../../../../models/IOrder_Progress_State";
import ISupplierModel from "../../../../models/ISupplierModel";
import OrderStatesEnum from "../../../../models/OrderStatesEnum";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { WorkflowStepList } from "./WorkflowStepList";
import { WorkflowStepPanel } from "./WorkflowStepPanel";
import { WorkflowStepGuide } from "./WorkflowStepGuide";
import { WorkflowTracker } from "./WorkflowTracker";
import { IWorkflowStateOption, joinWorkflowSteps, newWorkflowStep, orderedWorkflowSteps, splitWorkflowSteps } from "./workflowSteps";
import "./WorkflowEditor.scss";

type Props = {
    /** The working steps, in the saved shape (`order_progress_states` or a workflow's `detail`). */
    steps: IOrder_Progress_State[];
    supplierModel: ISupplierModel;
    /** Called with the whole renumbered list after every change. */
    onChange: (steps: IOrder_Progress_State[]) => void;
    /** The last saved steps, so changed steps can carry an Edited mark. */
    savedSteps?: IOrder_Progress_State[];
};

/**
 * The order workflow editor: the steps as a drag-to-reorder list with a summary chip per step on the
 * left, the selected step's settings grouped on the right, and a plain-words guide underneath.
 * Used for the single workflow and for each workflow when multiple workflows are on.
 */
export const WorkflowEditor = (props: Props) => {
    const parts = useMemo(() => splitWorkflowSteps(props.steps), [props.steps]);
    const ordered = useMemo(() => joinWorkflowSteps(parts), [parts]);

    const firstEditable = parts.editable[0]?.order_state_id ?? parts.preliminary?.order_state_id ?? null;
    const [selectedId, setSelectedId] = useState<OrderStatesEnum | null>(firstEditable);

    // Keep the selection on a step that still exists (after a remove or an outside reset).
    useEffect(() => {
        if (selectedId !== null && !ordered.some((step) => step.order_state_id === selectedId)) {
            setSelectedId(firstEditable);
        }
    }, [ordered, selectedId, firstEditable]);

    const selected = ordered.find((step) => step.order_state_id === selectedId) || null;

    const orderedSaved = useMemo(() => orderedWorkflowSteps(props.savedSteps || []), [props.savedSteps]);

    /** A step without its position and its off flags, so toggling an option on and off is no edit. */
    const normalizeStep = (step: IOrder_Progress_State) =>
        _.omitBy(_.omit(step, ["step_no", "isFinalStep"]), (value) => _.isNil(value) || value === false || value === "");

    const isStepEdited = useCallback(
        (step: IOrder_Progress_State) => {
            if (!props.savedSteps) {
                return false;
            }

            const savedIndex = orderedSaved.findIndex((candidate) => candidate.order_state_id === step.order_state_id);
            const workingIndex = ordered.findIndex((candidate) => candidate.order_state_id === step.order_state_id);

            if (savedIndex < 0) {
                return true;
            }

            return savedIndex !== workingIndex || !_.isEqual(normalizeStep(orderedSaved[savedIndex]), normalizeStep(ordered[workingIndex]));
        },

        [props.savedSteps, orderedSaved, ordered]
    );

    const emit = (next: typeof parts) => props.onChange(joinWorkflowSteps(next));

    const updateStep = (id: OrderStatesEnum, patch: Partial<IOrder_Progress_State>) => {
        const apply = (step: IOrder_Progress_State) => (step.order_state_id === id ? { ...step, ...patch } : step);

        emit({
            preliminary: parts.preliminary ? apply(parts.preliminary) : null,
            editable: parts.editable.map(apply),
            fulfillment: parts.fulfillment.map(apply),
            final: parts.final ? apply(parts.final) : null,
        });
    };

    const reorder = (fromIndex: number, toIndex: number) => {
        const editable = [...parts.editable];
        const [moved] = editable.splice(fromIndex, 1);

        editable.splice(toIndex, 0, moved);
        emit({ ...parts, editable });
    };

    const addStep = (option: IWorkflowStateOption) => {
        const step = newWorkflowStep(option);
        const editable = [...parts.editable];
        const after = editable.findIndex((candidate) => candidate.order_state_id === selectedId);

        editable.splice(after >= 0 ? after + 1 : editable.length, 0, step);
        emit({ ...parts, editable });
        setSelectedId(step.order_state_id);
    };

    const removeStep = (id: OrderStatesEnum) => {
        const index = parts.editable.findIndex((step) => step.order_state_id === id);
        const editable = parts.editable.filter((step) => step.order_state_id !== id);
        const final = parts.final && parts.final.order_state_id === id ? null : parts.final;

        emit({ ...parts, editable, final });
        setSelectedId(editable[Math.max(0, Math.min(index, editable.length - 1))]?.order_state_id ?? firstEditable);
    };

    /** Moves a step into the final slot (the previous final step rejoins the editable list) or back out of it. */
    const setFinal = (id: OrderStatesEnum, isFinal: boolean) => {
        if (isFinal) {
            const step = parts.editable.find((candidate) => candidate.order_state_id === id);

            if (!step) {
                return;
            }

            const editable = parts.editable.filter((candidate) => candidate.order_state_id !== id);

            if (parts.final) {
                editable.push({ ...parts.final, isFinalStep: false });
            }

            emit({ ...parts, editable, final: { ...step, isFinalStep: true } });
        } else if (parts.final && parts.final.order_state_id === id) {
            emit({ ...parts, editable: [...parts.editable, { ...parts.final, isFinalStep: false }], final: null });
        }
    };

    return (
        <div className={"WorkflowEditor"} data-testid={"workflow-editor"}>
            <div className={"WorkflowEditor-preview"}>
                <OwLabel title={"preview"} className={"WorkflowEditor-caption"} noBottomMargin={true} />
                <WorkflowTracker steps={ordered} currentId={selectedId} onPick={setSelectedId} />
            </div>

            <div className={"WorkflowEditor-grid"}>
                <WorkflowStepList
                    parts={parts}
                    allSteps={ordered}
                    supplierModel={props.supplierModel}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    onReorder={reorder}
                    onAdd={addStep}
                    isStepEdited={isStepEdited}
                />

                <WorkflowStepPanel
                    step={selected}
                    allSteps={ordered}
                    supplierModel={props.supplierModel}
                    isFinal={!!parts.final && !!selected && parts.final.order_state_id === selected.order_state_id}
                    canBeFinal={!!selected && parts.editable.some((step) => step.order_state_id === selected.order_state_id)}
                    onUpdate={updateStep}
                    onRemove={removeStep}
                    onSetFinal={setFinal}
                />
            </div>

            {selected ? <WorkflowStepGuide step={selected} allSteps={ordered} supplierModel={props.supplierModel} /> : null}
        </div>
    );
};

export default WorkflowEditor;
